import { TOAST_TIMEOUT } from 'src/config/Toast';
import store from 'src/store';
import { type RegionForm, type RegionFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { createToast } from 'src/store/toastsSlice';
import { type AccommodationTypeName } from 'src/types/accommodationTypes';
import apiClient from 'src/utils/apiClient';

import rtwClient from '../apiService';

import type {
  MeasureUnit,
  NonPhysicalTask,
  NonPhysicalTaskDTO,
  RegionTask,
  RegionTasksAndGroupsDTO,
  Task,
  TaskGroupDTO,
  TaskGroupDTOPayload,
} from './types';

// **** Functions **** //

/**
 * Fetch units of measure
 */
async function fetchMeasureUnits(): Promise<MeasureUnit[]> {
  const { data } = await apiClient.get('/measureUnits');
  return data;
}

/**
 * Fetch regions
 */
async function fetchRegions(): Promise<RegionForm[]> {
  const { data } = await apiClient.get('/regions');
  return data;
}

/**
 * Fetch all tasks
 */
async function fetchTasks(): Promise<Task[]> {
  const { data } = await apiClient.get('/tasks');
  return data;
}

/**
 * Create or update task (create if id not provided)
 */
async function createOrUpdateTask(data: Task): Promise<Task> {
  const res = await apiClient.post('/tasks', data);
  if (res) {
    store.dispatch(
      createToast({
        type: 'success',
        message: data.id ? 'Updated task successfully' : 'Created task successfully',
        timeout: TOAST_TIMEOUT,
      }),
    );
  }
  return res.data;
}

/**
 * Get RegionTasks by regionId.
 */
async function fetchRegionTasksByRegionId(regionId: number, axiosClient = apiClient): Promise<RegionTask[]> {
  const { data } = await axiosClient.get(`/regionTasks/${regionId}`);
  return data;
}

/**
 * Update a list of RegionTasks
 */
async function updateRegionTasks(data: RegionTask[]): Promise<RegionTask[]> {
  const res = await rtwClient.post<RegionTask[]>('/regionTasks', data);
  return res.data;
}

/**
 * Update RestrictionForm tasks
 */
async function updateRestrictionForm(data: RegionFormTableRow): Promise<RegionForm> {
  const res = await rtwClient.post<RegionForm>('/regions', {
    id: data.id,
    name: data.name,
    restrictionFormName: data.formName,
    restrictionFormActive: data.isActive,
    restrictionFormDescription: data.description,
  });
  return res.data;
}

/**
 * Fetch all tasks
 */
async function fetchTaskGroups(): Promise<TaskGroupDTO[]> {
  const { data } = await apiClient.get('/taskGroups');
  return data;
}

/**
 * Delete a task group by id
 */
async function deleteTaskGroup(id: number): Promise<void> {
  await apiClient.delete(`/taskGroups/${id}`);
  store.dispatch(
    createToast({
      type: 'success',
      message: 'Deleted task group successfully',
      timeout: TOAST_TIMEOUT,
    }),
  );
}

/**
 * Create or update task (create if id not provided)
 */
async function addOrUpdateTaskGroup(data: TaskGroupDTOPayload): Promise<TaskGroupDTO | null> {
  const res = await apiClient.post('/taskGroups', data);
  if (!!res && res.data) {
    store.dispatch(
      createToast({
        type: 'success',
        message: `${data.id ? 'Updated' : 'Created'} task group successfully`,
        timeout: TOAST_TIMEOUT,
      }),
    );
    return res.data as TaskGroupDTO;
  }
  return null;
}

/**
 * For a region, fetch all taskGroups and tasks. Tasks part of a group will be nested under their respective task.
 */
async function getRegionGroupsAndTasksByRegionId(regionId: number, axiosClient = apiClient) {
  const res = await axiosClient.get<RegionTasksAndGroupsDTO>(`/regionTaskGroups/regions/${regionId}`);
  return res.data;
}

/**
 * Get modifications
 */
const fetchNonPhysicalTasks = async () => {
  const res = await rtwClient.get<NonPhysicalTask[]>('/nonPhysicalTasks');
  return res.data;
};

/**
 * Get modifications
 */
const fetchNonPhysicalTasksByRegion = async (regionId: number, accommodationType: AccommodationTypeName) => {
  const res = await rtwClient.get<NonPhysicalTask[]>(
    `/nonPhysicalTasks/regions/${regionId}?accommodationType=${accommodationType}`,
  );
  return res.data;
};

/**
 * Create or update modification
 */
const updateNonPhysicalTask = async (payload: NonPhysicalTaskDTO) => {
  const res = await rtwClient.put<NonPhysicalTask>(`/nonPhysicalTasks/${payload.id}`, payload);
  return res.data;
};

/**
 * Create or update modification
 */
const createNonPhysicalTask = async (payload: NonPhysicalTaskDTO) => {
  const res = await rtwClient.post<NonPhysicalTask>('/nonPhysicalTasks', payload);
  return res.data;
};

/**
 * Delete modification
 */
const deleteNonPhysicalTask = async (taskId: number) => {
  await rtwClient.delete(`/nonPhysicalTasks/${taskId}`);
};

// **** Export **** //

export default {
  fetchTasks,
  fetchMeasureUnits,
  fetchRegions,
  createOrUpdateTask,
  fetchRegionTasksByRegionId,
  updateRegionTasks,
  updateRestrictionForm,
  fetchTaskGroups,
  addOrUpdateTaskGroup,
  deleteTaskGroup,
  getRegionGroupsAndTasksByRegionId,
  fetchNonPhysicalTasks,
  fetchNonPhysicalTasksByRegion,
  createNonPhysicalTask,
  updateNonPhysicalTask,
  deleteNonPhysicalTask,
} as const;
